import { useEffect, useState } from "react";
import { Alert, Badge, Center, Group, Loader, Progress, Stack, Text, Title } from "@mantine/core";
import { IconAlertCircle, IconMoodEmpty } from "@tabler/icons-react";
import { useFetch } from "../../helpers/useFetch.js";

/**
 * Anonymní hodnocení vlastního oddílu.
 *
 * Vědomě ukazuje jen souhrn – u výběrových otázek rozpad odpovědí, u textových jejich
 * výpis. Backend nevrací jména ani jednotlivé respondenty a texty posílá promíchané,
 * takže nejde zjistit, co napsalo konkrétní dítě.
 */
function ChoiceQuestion({ question }) {
  const max = Math.max(...question.options.map((option) => option.count), 1);

  return (
    <Stack gap={6}>
      {question.options.map((option) => (
        <Group key={option.title} gap="sm" wrap="nowrap">
          <Text size="sm" className="w-24 shrink-0" c={option.other ? "dimmed" : undefined}>
            {option.title}
          </Text>
          <Progress value={(option.count / max) * 100} size="lg" radius="sm" className="flex-grow" />
          <Text size="sm" fw={600} className="w-8 shrink-0 text-right">
            {option.count}
          </Text>
        </Group>
      ))}
    </Stack>
  );
}

function TextQuestion({ question }) {
  if (!question.texts.length) {
    return (
      <Text size="sm" c="dimmed" fs="italic">
        Zatím nikdo neodpověděl.
      </Text>
    );
  }

  return (
    <Stack gap="xs">
      {question.texts.map((text, index) => (
        <Text key={index} size="sm" className="border-l-2 border-gray-600 pl-3">
          {text}
        </Text>
      ))}
    </Stack>
  );
}

export default function TeamSurveyResultsModal() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    useFetch("my-team/survey-responses")
      .then((res) => {
        if (res?.error) {
          setError(res.error);
        } else {
          setData(res.data);
        }
      })
      .catch(() => setError("Hodnocení se nepodařilo načíst."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} stroke={1.5} />} color="red">
        {error}
      </Alert>
    );
  }

  const surveys = data?.surveys ?? [];

  if (!surveys.length) {
    return (
      <Stack align="center" gap="xs" py="xl">
        <IconMoodEmpty size={40} stroke={1.5} opacity={0.5} />
        <Text fw={600}>Zatím žádné hodnocení</Text>
        <Text size="sm" c="dimmed" ta="center">
          Až děti z tvého oddílu vyplní dotazník, uvidíš tady jejich odpovědi.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="xl">
      <Text size="sm" c="dimmed">
        Odpovědi jsou anonymní – nejde zjistit, kdo je napsal.
      </Text>

      {surveys.map((survey) => (
        <Stack key={survey.id} gap="lg">
          <Group gap="xs">
            <Title order={4} className="font-headline">
              {survey.name}
            </Title>
            <Badge variant="light">
              {survey.total} {survey.total === 1 ? "odpověď" : survey.total < 5 ? "odpovědi" : "odpovědí"}
            </Badge>
          </Group>

          {survey.questions.map((question) => (
            <Stack key={question.id} gap="xs">
              <Group gap="xs" align="baseline">
                <Text fw={600}>{question.text}</Text>
                <Text size="xs" c="dimmed">
                  ({question.answered})
                </Text>
              </Group>
              {question.kind === "choice" ? (
                <ChoiceQuestion question={question} />
              ) : (
                <TextQuestion question={question} />
              )}
            </Stack>
          ))}
        </Stack>
      ))}
    </Stack>
  );
}
